import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/navigation/PublicHeader';
import SearchBar from './components/SearchBar';
import DriverResultCard from './components/DriverResultCard';
import SearchHistory from './components/SearchHistory';
import EmptyState from './components/EmptyState';
import ErrorState from './components/ErrorState';
import SyncStatusIndicator from './components/SyncStatusIndicator';
import Icon from '../../components/AppIcon';

import { supabase } from '../../lib/supabase';

const DriverLookupPortal = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);

  useEffect(() => {
    const savedHistory = localStorage.getItem('driverSearchHistory');
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
  }, []);

  const handleSearch = async (e164Phone, formattedPhone) => {
    setIsLoading(true);
    setError(null);
    setSearchResult(null);

    try {
      // Call Supabase RPC function with normalized E.164 phone
      const { data, error: rpcError } = await supabase?.rpc('get_driver_snapshot', {
        p_phone: e164Phone
      });

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        setError(`Database error: ${rpcError?.message || 'Failed to fetch driver information'}`);
        return;
      }

      // Check if we got any results
      if (!data || data?.length === 0) {
        setError('Driver not found. No active driver record matches that phone number.');
        return;
      }

      // Transform the database result to match the component's expected format
      const driverData = data?.[0];
      const driver = {
        name: driverData?.driver_name || '',
        phone: formattedPhone,
        e164Phone: e164Phone,
        unitNumber: driverData?.unit_number || null,
        vin: driverData?.vin || null,
        yardName: driverData?.yard_name || null,
        yardAddress: driverData?.yard_address || '',
        yardId: driverData?.yard_id || null,
        unitOverride: driverData?.unit_override || '',
        vinOverride: driverData?.vin_override || '',
        status: 'active'
      };

      setSearchResult(driver);

      // Save to search history
      const historyItem = {
        phone: formattedPhone,
        e164Phone: e164Phone,
        timestamp: new Date()?.toISOString()
      };

      const updatedHistory = [
      historyItem,
      ...searchHistory?.filter((h) => h?.e164Phone !== e164Phone)]?.
      slice(0, 10);

      setSearchHistory(updatedHistory);
      localStorage.setItem('driverSearchHistory', JSON.stringify(updatedHistory));

    } catch (err) {
      console.error('Search error:', err);
      setError(`An unexpected error occurred: ${err?.message || 'Please try again'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectHistory = (historyItem) => {
    handleSearch(historyItem?.e164Phone, historyItem?.phone);
  };

  const handleClearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('driverSearchHistory');
  };

  const handleRetry = () => {
    setError(null);
    setSearchResult(null);
  };

  const handleCallDispatch = () => {
    window.location.href = 'tel:+18005550100';
  };

  const handleDriverUpdated = (newE164Phone) => {
    // newE164Phone is already in E.164 format from DriverResultCard's handleSave
    const e164 = newE164Phone || searchResult?.e164Phone;
    // Reconstruct a display-formatted phone for the history/display
    const digits = e164?.replace(/\D/g, '') || '';
    const formattedPhone =
      digits?.length === 11 && digits?.startsWith('1')
        ? `(${digits?.slice(1, 4)}) ${digits?.slice(4, 7)}-${digits?.slice(7)}`
        : e164;
    handleSearch(e164, formattedPhone);
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="pt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 space-y-6">
                <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h1 className="text-2xl font-semibold text-foreground mb-2">Driver Lookup</h1>
                      <p className="text-sm text-muted-foreground">
                        Search by phone number to view driver assignments and vehicle information
                      </p>
                    </div>
                    <SyncStatusIndicator />
                  </div>
                  
                  <SearchBar onSearch={handleSearch} isLoading={isLoading} />
                </div>

                {isLoading &&
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-4 animate-pulse" style={{ backgroundColor: 'rgba(6,83,149,0.1)' }}>
                      <Icon name="Search" size={32} strokeWidth={2} style={{ color: '#065395' }} />
                    </div>
                    <p className="text-sm text-muted-foreground">Searching driver records...</p>
                  </div>
                }

                {!isLoading && searchResult &&
                <DriverResultCard driver={searchResult} onCallDispatch={handleCallDispatch} onDriverUpdated={handleDriverUpdated} />
                }

                {!isLoading && error &&
                <ErrorState message={error} onRetry={handleRetry} />
                }

                {!isLoading && !searchResult && !error &&
                <EmptyState />
                }

                <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(6,83,149,0.05)', border: '1px solid rgba(6,83,149,0.2)' }}>
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} strokeWidth={2} style={{ color: '#065395' }} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground mb-1">Quick Access Tips</p>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• Phone numbers are automatically formatted as you type</li>
                        <li>• Recent searches are saved for quick re-access</li>
                        <li>• Only active driver assignments are displayed</li>
                        <li>• Contact support at (800) 555-0100 for assistance</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-80 space-y-6">
                <SearchHistory
                  history={searchHistory}
                  onSelectHistory={handleSelectHistory}
                  onClearHistory={handleClearHistory} />
                

                <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="Phone" size={20} color="var(--color-success)" strokeWidth={2} />
                    <h3 className="text-sm font-semibold text-foreground">Emergency Contact</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Dispatch Center</p>
                      <a href="tel:+18005550100" className="text-sm font-medium hover:underline" style={{ color: '#065395' }}>
                        (800) 555-0100
                      </a>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Fleet Operations</p>
                      <a href="tel:+18005550200" className="text-sm font-medium hover:underline" style={{ color: '#065395' }}>
                        (800) 555-0200
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <footer className="bg-card border-t border-border mt-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} RPM Diesel Driver Portal. All rights reserved.
              </span>
            </div>
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <a href="tel:+18005550100" className="hover:text-foreground transition-hover">Support</a>
              <span>•</span>
              <span>Version 1.0.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default DriverLookupPortal;